import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { TaskStatus, TaskType } from '@monorepo/shared';
import { taskStatusDisplay, taskTypeLabel } from './taskDisplay';

export interface TaskUserOption {
  id: string;
  label: string;
}

interface TasksFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: TaskType | '';
  onTypeFilterChange: (value: TaskType | '') => void;
  statusFilter: TaskStatus | '';
  onStatusFilterChange: (value: TaskStatus | '') => void;
  showUserFilter: boolean;
  userOptions: TaskUserOption[];
  userFilter: string;
  onUserFilterChange: (value: string) => void;
  onReset: () => void;
}

const selectClassName =
  'px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 focus:ring-2 focus:ring-primary focus:border-primary bg-white';

export const TasksFilters = ({
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  showUserFilter,
  userOptions,
  userFilter,
  onUserFilterChange,
  onReset,
}: TasksFiltersProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-4 sm:px-6">
      <div className="relative flex-1 min-w-[220px]">
        <MagnifyingGlassIcon
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('tasks.filters.search_placeholder')}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-primary"
        />
      </div>

      <select
        value={typeFilter}
        onChange={(e) => onTypeFilterChange(e.target.value as TaskType | '')}
        className={selectClassName}
      >
        <option value="">{t('tasks.filters.type_all')}</option>
        {Object.values(TaskType).map((type) => (
          <option key={type} value={type}>
            {t(taskTypeLabel[type])}
          </option>
        ))}
      </select>

      {showUserFilter && (
        <select
          value={userFilter}
          onChange={(e) => onUserFilterChange(e.target.value)}
          className={selectClassName}
        >
          <option value="">{t('tasks.filters.user_all')}</option>
          {userOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      <select
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value as TaskStatus | '')}
        className={selectClassName}
      >
        <option value="">{t('tasks.filters.status_all')}</option>
        {Object.values(TaskStatus).map((status) => (
          <option key={status} value={status}>
            {t(taskStatusDisplay[status].label)}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={onReset}
        className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-opacity-90 transition"
      >
        {t('tasks.filters.reset')}
      </button>
    </div>
  );
};
